// AiBridgeModal — the "connect your local AI" setup, as a shell-root MODAL
// OVERLAY rather than an inline card inside the AI-settings dropdown.
//
// Why a root-mounted modal and not inline: AiSettingsPanel renders inside the
// @symbo.ls/dropdown Panel, which uses `transform` + `overflow: auto`. A
// `position: fixed` child there is clipped/mis-positioned by that transformed
// ancestor, and growing the panel inline shifts the whole dropdown. Mounting
// this once at the shell root (beside ModalRoot/DropdownRoot in app.js) and
// gating it on `root._aiBridgeSetupOpen` gives a clean centered overlay with
// zero dropdown layout shift. Both AI-settings surfaces (canvas ParamsBtn
// dropdown + sidebar SettingsDrawer) just flip the flag.
//
// Reads bridge state off root (bridgeStatus / bridgeUrl / agent) and drives the
// local simone-bridge via the registered `configureBridge` function — same
// plumbing the old inline card used.

export const AiBridgeModal = {
  // FA204 / frank trap — isLocal used to be a bare module-scope const read
  // from inside `if` (a serialized factory); it reads as undefined once this
  // component is frank.toJSON'd for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.isLocal`.
  scope: {
    isLocal: (s) => (s.root.targetMode || 'general') === 'local',
    // THE close path — the veil, Escape and Done all run it. The `if:` gate
    // unmounts this element WITHOUT a lifecycle hook on this runtime
    // (measured: an if-flip fires only onInit, never onRemove), so focus goes
    // back to the opener here, through the kit's AppDialog lifecycle function
    // bound to the modal root.
    close(el, s) {
      s.root.update({ _aiBridgeSetupOpen: false })
      const modal = (typeof el.lookup === 'function' && el.lookup('AiBridgeModal')) || el
      if (modal && typeof modal.call === 'function') modal.call('appDialogSync', false)
    }
  },
  if: (el, s) => !!s.root._aiBridgeSetupOpen && el.scope.isLocal(s),
  // A typed URL is a DRAFT in this modal's own state until the user connects.
  // The input used to write `root.bridgeUrl` on every keystroke — a shared
  // key the status line, the bridge and the catalog specimen all read (the
  // specimen caught it, 2026-09-24). Connect hands the draft to
  // `configureBridge`, which writes the root key itself. A draft the user
  // never connects is dropped with the modal.
  state: { draftUrl: '', draftTouched: false },
  tag: 'div',
  position: 'fixed',
  inset: '0',
  zIndex: '10600',
  // THE blocking veil (DESIGN_SPEC §6.3): the `backdrop` theme — the scrim
  // fill AND its blur — fading in as the modal mounts. It was the bare
  // `scrim` fill, no blur.
  theme: 'backdrop',
  animationName: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'both',
  align: 'center center',
  padding: 'overlayGutter',
  // The phone sheet touches the bottom edge.
  '@mobileL': { padding: '0', alignItems: 'flex-end' },
  onClick: (e, el, s) => el.scope.close(el, s),
  // Escape closes the top-most overlay only: it stops here.
  onKeydown: (e, el, s) => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    el.scope.close(el, s)
  },
  // Focus moves in on open (the kit's AppDialog lifecycle: the first field,
  // a frame later). Only a REAL open — a catalog specimen that mounts this
  // with `if: true` must not steal the page's focus.
  onInit: (el, s) => {
    if (s.root && s.root._aiBridgeSetupOpen) el.call('appDialogSync', true)
  },

  // The centre sheet (§6.1): opaque `dialog`, `radiusSheet`, the `overlay`
  // elevation, `overlayMd` wide (was a raw 460px on the `modal` surface),
  // rising on the spring; the phone gets the bottom sheet.
  Sheet: {
    onClick: (e) => e.stopPropagation(),
    role: 'dialog',
    ariaModal: 'true',
    ariaLabelledby: 'aiBridgeTitle',
    tabindex: '-1',
    onKeydown: (e, el) => el.call('appDialogTrapTab', e),
    flow: 'y',
    gap: 'A',
    boxSizing: 'border-box',
    width: 'overlayMd',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    padding: 'B',
    theme: 'dialog',
    round: 'radiusSheet',
    shadow: 'overlay',
    animationName: 'modalIn',
    animationDuration: 'D',
    animationTimingFunction: 'springBezier',
    animationFillMode: 'both',
    '@mobileL': {
      width: '100%',
      maxHeight: '92%',
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
      animationName: 'fadeInUpShort'
    },

    Header: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      Title: {
        tag: 'h3',
        id: 'aiBridgeTitle',
        text: '{{ ai.bridge.title | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        margin: '0',
        flex: '1'
      },
      // The dismiss — the kit's tonal capsule in its compact step (was a
      // hand-built button on the `tertiary` theme).
      Close: {
        extends: 'AppCapsuleBtnToolbar',
        compact: true,
        text: '{{ ai.bridge.done | polyglot }}',
        onClick: (e, el, s) => el.scope.close(el, s)
      }
    },

    Status: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      Dot: {
        boxSize: 'X',
        round: '50%',
        flexShrink: '0',
        // The status hue as its semantic pair (Rule 69) — the raw brand hues
        // fall under 3:1 as a mark on the light sheet.
        background: (el, s) => {
          const st = s.root.bridgeStatus
          if (st === 'open') return 'successInk'
          if (st === 'connecting') return 'accentInk'
          return 'dangerInk'
        }
      },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const st = s.root.bridgeStatus
          if (st === 'open') return 'Connected' + (s.root.agent ? ' · ' + s.root.agent : '')
          if (st === 'connecting') return 'Connecting…'
          return 'Not connected'
        }
      }
    },

    Steps: {
      tag: 'ol',
      margin: '0',
      padding: '0 0 0 A',
      color: 'caption',
      fontSize: 'Y',
      lineHeight: '1.6',
      Step1: { tag: 'li', text: '{{ ai.bridge.step1 | polyglot }}' },
      Step2: {
        tag: 'li',
        text: (el, s) =>
          'It listens for the workspace on ' +
          ((s.draftTouched ? s.draftUrl : s.root.bridgeUrl) || 'ws://localhost:8765') +
          '.'
      },
      Step3: {
        tag: 'li',
        text: '{{ ai.bridge.step3 | polyglot }}'
      }
    },

    // The shared field kit (components/AppField.js): AppField owns the
    // caption, AppInput the control surface; this field keeps the behaviour
    // only. `label` is a FUNCTION prop — the kit also names the control with
    // it, and an attribute never resolves a template.
    AppField: {
      label: (el) => el.call('polyglot', 'ai.bridge.urlLabel'),
      AppInput: {
        type: 'text',
        placeholder: 'ws://localhost:8765',
        // the draft once the user types, else the saved URL; all three are
        // read on every run, so each one is tracked (FA515)
        value: (el, s) => {
          const saved = s.root.bridgeUrl
          const draft = s.draftUrl
          return (s.draftTouched ? draft : saved) || ''
        },
        onInput: (e, el, s) =>
          s.update({ draftUrl: e.target.value, draftTouched: true }, { preventFetch: true })
      }
    },

    Actions: {
      flow: 'x',
      gap: 'Z',
      align: 'center flex-end',
      // The one action — the kit's ink primary (was a hand-built button on
      // the `documentInverted` theme).
      Connect: {
        extends: 'AppCapsuleBtnPrimary',
        text: (el, s) => (s.root.bridgeStatus === 'open' ? 'Reconnect' : 'Connect'),
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          try {
            const url = (s.draftTouched ? s.draftUrl : s.root.bridgeUrl) || 'ws://localhost:8765'
            el.call('configureBridge', url)
          } catch (_) {}
        }
      }
    }
  }
}

export default AiBridgeModal
